if (!customElements.get('quick-add-modal')) {
  /*
    Where the modal gets its markup.

    "Choose options" used to fetch the entire product document and keep one
    element out of it. Measured on a phone-shaped viewport that is 443 KB over
    the wire for a modal showing a picture, a price and two size pills: the age
    gate, the subscription app's six hundred references, the reviews widget, the
    FAQ, the footer and the related products row all travelled so they could be
    thrown away by the next line. sections/quick-add-product.liquid renders the
    buy box and nothing else, and the Section Rendering API serves it by file
    name, the same way the header's type-ahead is served.

    If that section is not in the theme the request answers with the page
    instead, which is why the fetch below checks what came back before it trusts
    it. These live inside the guard because a merchant can put two sections with
    quick add on one page, and this file is written to survive being loaded
    twice.
  */
  const QUICK_ADD_SECTION = 'quick-add-product';

  // A pointer crossing a grid of cards passes over every button on its way
  // somewhere else, so nothing is asked for until it has settled.
  const QUICK_ADD_WARM_DELAY = 150;

  // And a warmed answer is only good for as long as the quantity already in the
  // cart, which is printed into that markup, can be trusted not to have moved.
  const QUICK_ADD_WARM_LIFE = 15000;

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.warmed = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      connectedCallback() {
        // The parent moves this element to the end of the body, which
        // disconnects and reconnects it, so both it and this guard their work.
        super.connectedCallback();
        if (this.warmupBound) return;
        this.warmupBound = true;

        const opener = document.querySelector(`[data-modal="#${this.id}"] button`);
        if (!opener) return;

        let timer = null;
        const settle = () => {
          clearTimeout(timer);
          timer = setTimeout(() => this.warm(opener.getAttribute('data-product-url')), QUICK_ADD_WARM_DELAY);
        };
        const leave = () => clearTimeout(timer);

        opener.addEventListener('pointerenter', settle);
        opener.addEventListener('focus', settle);
        opener.addEventListener('pointerleave', leave);
        opener.addEventListener('blur', leave);
      }

      // Asking early is only a kindness on a connection that is not being paid
      // for by the megabyte.
      warm(productUrl) {
        if (!productUrl || this.isWarm(productUrl)) return;

        const connection = navigator.connection;
        if (connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''))) return;

        const request = this.requestProduct(productUrl);
        this.warmed = { url: productUrl, at: Date.now(), request };
        request.catch(() => {
          this.warmed = null;
        });
      }

      isWarm(productUrl) {
        return !!this.warmed && this.warmed.url === productUrl && Date.now() - this.warmed.at < QUICK_ADD_WARM_LIFE;
      }

      requestProduct(productUrl) {
        const separator = productUrl.includes('?') ? '&' : '?';

        return fetch(`${productUrl}${separator}section_id=${QUICK_ADD_SECTION}`)
          .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
          .then((responseText) => QuickAddModal.readProduct(responseText))
          .catch(() =>
            // Either the section is not in the theme or the request failed. The
            // whole document still holds the same element, so ask for it the
            // long way round rather than open an empty modal.
            fetch(productUrl)
              .then((response) => response.text())
              .then((responseText) => QuickAddModal.readProduct(responseText))
          );
      }

      static readProduct(responseText) {
        const productElement = new DOMParser().parseFromString(responseText, 'text/html').querySelector('product-info');
        if (!productElement) throw new Error('No product-info in the response');
        return productElement;
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        const productUrl = opener.getAttribute('data-product-url');
        const request = this.isWarm(productUrl) ? this.warmed.request : this.requestProduct(productUrl);
        this.warmed = null;

        request
          .then((productElement) => {
            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
          })
          .catch((error) => {
            // Neither the section nor the page answered, so there is nothing to
            // open the modal with. This button was a shortcut to the product
            // page in the first place, and a shopper whose connection has gone
            // gets the browser's own answer instead of a button that spins and
            // then pretends nothing happened.
            console.error(error);
            window.location.href = productUrl;
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
